/**
 * Day punch map — the data layer behind the Timesheet Review "Map" button.
 *
 * v1 could plot every punch location for a day (TimesheetPage.openDayMap); v2 only
 * ever surfaced a map when a punch raised a GPS exception. This rebuilds the day walk
 * against the payload the review console already fetches: getEmployeeTimesheet returns
 * in/outLatitude/Longitude on both punch tables and the site + geofence on each hourly
 * group, so nothing here needs a new endpoint.
 *
 * Ported from docs/featureBriefs/design_handoff_day_punch_map/hifi/day-map.js. That
 * prototype SYNTHESISED its coordinates (SITES / FAR / offsetPoint / siteFor), which is
 * why it paired punches on clock time alone — it had to decide which fabricated points
 * to make identical. Real punches carry real coordinates, so proximity is the actual
 * test here and all of the coordinate-inventing machinery is gone.
 */
import { Day, HourlyProject, Project, TimeEntry, TimeEntryHourly } from '../interfaces/EmployeeInterface';
import { PunchType, TimeEntryType } from '../interfaces/TimesheetInterface';
import { distanceMiles, hasValidGps } from './GpsUtil';

/** Two recorded points this close are the same physical spot (~106 ft). */
const SAME_SPOT_MILES = 0.02;
/** An out→in pair beyond this gap is a return trip, not one stop. */
const SAME_SPOT_MAX_GAP_MIN = 45;

export interface PunchSite {
  name: string;
  altID: string | null;
  lat: number;
  lng: number;
  /** Null when the job has no geofence configured — draw no circle. */
  radiusMiles: number | null;
}

/**
 * One side of one punch. A punch row yields up to two of these; the "in" and "out"
 * of the same entry are independent locations and are never assumed to match.
 */
export interface PunchEvent {
  key: string;
  side: 'in' | 'out';
  /** Local wall-clock label, e.g. "6:30a". */
  time: string;
  /** Minutes past local midnight — ordering and the same-spot gap test. */
  minutes: number;
  jobName: string;
  costCode: string;
  isMeal: boolean;
  /** Manually added or edited: the device never recorded a position. */
  isManual: boolean;
  /** Null ⇒ listed greyed, never plotted, never counted in the fit. */
  lat: number | null;
  lng: number | null;
  /** Null for bare piece punches — TimeEntry has no project at all. */
  site: PunchSite | null;
  /** Haversine to the site; display only. Null when there is no site. */
  distanceMiles: number | null;
  /**
   * The STORED gpsException flag, never a recomputed distance. The map, the Alerts
   * badge and the GPS Exceptions page must tell one story, and a radius edited after
   * the fact would otherwise make them disagree.
   */
  outsideGeofence: boolean;
  message: string | null;
  /** 1-based pin number; null for events with no coordinates. */
  number: number | null;
}

/** A cluster of co-located consecutive events — one pin, one list row. */
export interface PunchStop {
  events: PunchEvent[];
  lat: number;
  lng: number;
  site: PunchSite | null;
  distanceMiles: number | null;
  outsideGeofence: boolean;
  message: string | null;
}

// ── time helpers ─────────────────────────────────────────────────────────────
// Punches are stored UTC with the local offset alongside; shifting by the offset and
// then reading UTC fields yields the wall-clock time the employee actually saw. Same
// convention as the review console and GpsExceptionsPage.

function applyOffset(utcISO: string, offsetStr: string | null | undefined): Date {
  const base = new Date(utcISO);
  const m = (offsetStr ?? '').match(/([+-])(\d{2}):(\d{2})/);
  if (!m) return base;
  const sign = m[1] === '+' ? 1 : -1;
  return new Date(base.getTime() + sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) * 60000);
}

function formatClock(d: Date): string {
  const h = d.getUTCHours() % 12 || 12;
  const m = d.getUTCMinutes().toString().padStart(2, '0');
  return `${h}:${m}${d.getUTCHours() >= 12 ? 'p' : 'a'}`;
}

// ── formatting ───────────────────────────────────────────────────────────────

/** Feet under 0.19 mi (to the nearest 10), else miles — 2 dp under 1 mi, else 1 dp. */
export function formatDistance(mi: number | null | undefined): string | null {
  if (mi == null || !Number.isFinite(mi)) return null;
  if (mi < 0.19) return `${Math.round((mi * 5280) / 10) * 10} ft`;
  return `${mi.toFixed(mi < 1 ? 2 : 1)} mi`;
}

// ── the day walk ─────────────────────────────────────────────────────────────

function siteFromProject(p: Project | null | undefined): PunchSite | null {
  if (!p || !hasValidGps(p.latitude, p.longitude)) return null;
  return {
    name: p.name,
    altID: p.altProjectID ?? null,
    lat: p.latitude as number,
    lng: p.longitude as number,
    radiusMiles: p.radiusMiles ?? null,
  };
}

/**
 * Build both sides of one punch entry.
 *
 * `jobName`/`costCode` come from the caller because on hourly punches the API hoists
 * project and account off the entry and onto the enclosing group — they are not on the
 * punch object. Raw TimeEntry punches have no project at all, so `site` is null and
 * they get no distance and can never be flagged.
 */
function eventsForEntry(
  entry: TimeEntry | TimeEntryHourly,
  idPrefix: string,
  jobName: string,
  costCode: string,
  site: PunchSite | null
): PunchEvent[] {
  const isMeal = entry.type === TimeEntryType.Meal;
  const exc = entry.gpsException;
  const out: PunchEvent[] = [];

  const sides: Array<{
    side: 'in' | 'out';
    iso: string | null;
    offset: string | null;
    lat?: number;
    lng?: number;
    punchType: number;
    flagged: boolean;
    message: string | null;
  }> = [
    {
      side: 'in',
      iso: entry.inDateTime ?? null,
      offset: entry.inDateTimeOffset ?? null,
      lat: entry.inLatitude,
      lng: entry.inLongitude,
      punchType: entry.punchTypeIn,
      flagged: !!exc?.inException,
      message: exc?.inException ? (exc.inExceptionMessage ?? null) : null,
    },
    {
      side: 'out',
      iso: entry.outDateTime ?? null,
      offset: entry.outDateTimeOffset ?? null,
      lat: entry.outLatitude,
      lng: entry.outLongitude,
      punchType: entry.punchTypeOut,
      flagged: !!exc?.outException,
      message: exc?.outException ? (exc.outExceptionMessage ?? null) : null,
    },
  ];

  for (const s of sides) {
    if (!s.iso) continue; // a missing clock-out is not a location
    const local = applyOffset(s.iso, s.offset);
    if (Number.isNaN(local.getTime())) continue;
    const plottable = hasValidGps(s.lat, s.lng);
    out.push({
      key: `${idPrefix}-${s.side}`,
      side: s.side,
      time: formatClock(local),
      minutes: local.getUTCHours() * 60 + local.getUTCMinutes(),
      jobName,
      costCode,
      isMeal,
      isManual: s.punchType === PunchType.ManualAdd || s.punchType === PunchType.ManualEdit,
      lat: plottable ? (s.lat as number) : null,
      lng: plottable ? (s.lng as number) : null,
      site,
      distanceMiles:
        plottable && site ? distanceMiles(s.lat as number, s.lng as number, site.lat, site.lng) : null,
      // A punch with no site cannot be outside a geofence, whatever the flag says.
      outsideGeofence: !!site && s.flagged,
      message: site ? s.message : null,
      number: null,
    });
  }
  return out;
}

/** Job label for an hourly group. Mirrors the punch table's precedence, minus the
 *  service-order/CSW subtleties the map has no room to show. */
function hourlyJobName(hp: HourlyProject): string {
  const csw = hp.customerServiceWarranty;
  if (csw?.customerServiceWarrantyID != null && csw.customerServiceWarrantyID !== 0) {
    return csw.name ?? 'Customer Service';
  }
  return hp.project?.name ?? '';
}

function hourlyCostCode(hp: HourlyProject): string {
  const a = hp.account;
  if (!a) return '';
  return [a.altAccountCodeID, a.name].filter((v) => v != null && v !== '').join(' ');
}

/**
 * Every punch on the day, both sides, in clock order, with plottable events numbered
 * 1..n. Events with no coordinates keep their place in the list but take no number.
 */
export function buildPunchEvents(day: Day | null | undefined): PunchEvent[] {
  if (!day) return [];
  const events: PunchEvent[] = [];

  // Raw TimeEntry punches: piece work and meals. This table has no ProjectID column,
  // so there is no site, no geofence and no distance — on WCDP/NPM that is most of the
  // piece workforce, and inventing a job for them would be a guess, not data.
  (day.timeEntries ?? []).forEach((entry, i) => {
    const isMeal = entry.type === TimeEntryType.Meal;
    events.push(...eventsForEntry(entry, `te-${i}`, isMeal ? 'Meal Break' : '', '', null));
  });

  (day.hourlyProjects ?? []).forEach((hp, hi) => {
    const site = siteFromProject(hp.project);
    const jobName = hourlyJobName(hp);
    const costCode = hourlyCostCode(hp);
    (hp.timeEntriesHourly ?? []).forEach((entry, ei) => {
      // Drive time is a derived allowance, not a place someone stood.
      if (entry.driveTimeEntryID != null) return;
      const isMeal = entry.type === TimeEntryType.Meal;
      events.push(
        ...eventsForEntry(
          entry,
          `hp-${hi}-${ei}`,
          isMeal ? 'Meal Break' : jobName,
          isMeal ? '' : costCode,
          isMeal ? null : site
        )
      );
    });
  });

  events.sort((a, b) => a.minutes - b.minutes);
  let n = 0;
  for (const e of events) {
    if (e.lat != null && e.lng != null) e.number = ++n;
  }
  return events;
}

/**
 * Collapse consecutive out→in events recorded at the same physical spot into one stop —
 * a meal taken where the employee was standing, or a job transfer punched on the spot.
 * Without this they stack invisibly on top of each other at map scale.
 *
 * Pairing is proximity first (the coordinates genuinely match), bounded by time so a
 * return to the same yard hours later does not read as one stop. Flagged punches never
 * pair: an out-of-geofence punch has to stay individually visible.
 */
export function clusterStops(events: PunchEvent[]): PunchStop[] {
  const stops: PunchStop[] = [];
  for (const e of events) {
    if (e.lat == null || e.lng == null) continue;
    const last = stops[stops.length - 1];
    const prev = last?.events[last.events.length - 1];
    const canMerge =
      !!last &&
      !!prev &&
      !e.outsideGeofence &&
      !last.outsideGeofence &&
      prev.side === 'out' &&
      e.side === 'in' &&
      e.minutes - prev.minutes <= SAME_SPOT_MAX_GAP_MIN &&
      distanceMiles(last.lat, last.lng, e.lat, e.lng) < SAME_SPOT_MILES;
    if (canMerge) {
      last!.events.push(e);
      continue;
    }
    stops.push({
      events: [e],
      lat: e.lat,
      lng: e.lng,
      site: e.site,
      distanceMiles: e.distanceMiles,
      outsideGeofence: e.outsideGeofence,
      message: e.message,
    });
  }
  return stops;
}

/** Pin label: "4" for a single event, "2–3" for a run. */
export function stopLabel(stop: PunchStop): string {
  const first = stop.events[0].number;
  const last = stop.events[stop.events.length - 1].number;
  return first === last ? String(first) : `${first}–${last}`;
}

/** Headline for a merged stop, read off the direction of travel. */
export function stopHeadline(stop: PunchStop): string | null {
  if (stop.events.length < 2) return null;
  const outLeg = stop.events[0];
  const inLeg = stop.events[stop.events.length - 1];
  if (inLeg.isMeal) return 'Off the clock — into meal break';
  if (outLeg.isMeal) return `Back from meal — onto ${inLeg.jobName || 'the job'}`;
  return `Job transfer — ${outLeg.jobName || 'job'} → ${inLeg.jobName || 'job'}`;
}

/**
 * Does this day have anything to plot? Drives whether the day card shows a Map button
 * at all, so it runs on every day card on every render — deliberately a short-circuiting
 * scan rather than a full buildPunchEvents.
 */
export function dayHasPunchCoords(day: Day | null | undefined): boolean {
  if (!day) return false;
  const anySide = (e: TimeEntry | TimeEntryHourly): boolean =>
    hasValidGps(e.inLatitude, e.inLongitude) || hasValidGps(e.outLatitude, e.outLongitude);
  if ((day.timeEntries ?? []).some(anySide)) return true;
  return (day.hourlyProjects ?? []).some((hp) =>
    (hp.timeEntriesHourly ?? []).some((e) => e.driveTimeEntryID == null && anySide(e))
  );
}
